import React, { useContext } from "react";
import { IconButton } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import currency from "currency.js";
import moment from "moment";
import * as XLSX from "xlsx";
import { UserContext } from "../../context/UserContext";
import { getItemMetadata, getItemUrl, getSaasWebsiteUrl } from "../../common/util";

function stripHtml(html) {
  if (!html) return "";
  return String(html)
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/(p|div|h[1-6]|li)>/gi, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function formatAmount(amount) {
  if (amount == null || amount === "") return "";
  return currency(amount).format({ symbol: "" });
}

function getVariantEntries(product) {
  if (!product.variants || typeof product.variants !== "object") return [];
  return Object.entries(product.variants).sort(([, a], [, b]) => (b.rank ?? 0) - (a.rank ?? 0));
}

function buildProductsSheet(products, domain) {
  const headers = ["productId", "title", "description", "category", "price", "url", "status"];
  const baseUrl = domain ? getSaasWebsiteUrl(domain) : "";
  const rows = products.map((product) => {
    const metadata = getItemMetadata(product);
    const categories = product.category || (product.type ? [product.type] : []);
    const minPrice = metadata.price?.min || 0;
    return [
      product.id || "",
      product.title || "",
      stripHtml(product.description),
      categories.filter(Boolean).join("|"),
      minPrice ? formatAmount(minPrice) : "",
      baseUrl && product.id ? `${baseUrl}${getItemUrl(product)}` : "",
      product.status || "",
    ];
  });
  return [headers, ...rows];
}

function getFieldName(variant, variantKey) {
  return variant?.name || variant?.field?.name || variantKey;
}

function isSelectField(variant) {
  if (variant?.field?.type === "select") return true;
  return Array.isArray(variant?.field?.options) && variant.field.options.length > 0;
}

function buildVariantsSheet(products) {
  const headers = ["productId", "name", "type", "required", "description"];
  const rows = [];
  for (const product of products) {
    for (const [variantKey, variant] of getVariantEntries(product)) {
      rows.push([
        product.id || "",
        getFieldName(variant, variantKey),
        variant?.field?.type || (isSelectField(variant) ? "select" : ""),
        variant?.required ? "true" : "false",
        stripHtml(variant?.description),
      ]);
    }
  }
  return [headers, ...rows];
}

function buildVariantOptionsSheet(products) {
  const headers = ["productId", "variant name", "option name", "description"];
  const rows = [];
  for (const product of products) {
    for (const [variantKey, variant] of getVariantEntries(product)) {
      if (!isSelectField(variant)) continue;
      const options = variant?.field?.options;
      if (!Array.isArray(options)) continue;
      for (const option of options) {
        rows.push([
          product.id || "",
          getFieldName(variant, variantKey),
          option?.name || "",
          stripHtml(option?.description),
        ]);
      }
    }
  }
  return [headers, ...rows];
}

function exportCatalogWorkbook(products, domain) {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.aoa_to_sheet(buildProductsSheet(products, domain)),
    "Products"
  );
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(buildVariantsSheet(products)), "Variants");
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.aoa_to_sheet(buildVariantOptionsSheet(products)),
    "Variant Options"
  );
  const dateStr = moment().format("YYYY-MM-DD");
  XLSX.writeFile(wb, `catalog-export-${dateStr}.xlsx`);
}

export default function ExportCatalogWidget({ products = [] }) {
  const { user } = useContext(UserContext);

  const exportCatalog = () => {
    if (!products.length) return;
    exportCatalogWorkbook(products, user.domain);
  };

  return (
    <IconButton onClick={exportCatalog} title="Download Excel" disabled={products.length === 0}>
      <FileDownloadOutlinedIcon />
    </IconButton>
  );
}
